import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import EventRepeatRoundedIcon from '@mui/icons-material/EventRepeatRounded';
import { Academy } from '../../hooks/api/academy';
import {
  LessonScheduleRule,
  LessonScheduleRulePayload,
  useCreateLessonScheduleRule,
  useDeleteLessonScheduleRule,
  useUpdateLessonScheduleRule,
} from '../../hooks/api/lessonScheduleRule';
import type { LessonAccessLevel } from '../../hooks/api/lesson';
import { useGetUsers, User } from '../../hooks/api/user';

interface InstructorScheduleTemplateEditorProps {
  academies: Academy[];
  academyId: number;
  createRequestKey: number;
  scheduleRules: LessonScheduleRule[];
}

interface ScheduleRuleFormValues {
  id?: number;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  type: string;
  accessLevel: LessonAccessLevel;
  professorId: number;
  academyId: number;
  maxSeats: number;
}

const WEEK_DAYS = [
  { value: 1, label: 'Lunes', shortLabel: 'Lun' },
  { value: 2, label: 'Martes', shortLabel: 'Mar' },
  { value: 3, label: 'Miércoles', shortLabel: 'Mié' },
  { value: 4, label: 'Jueves', shortLabel: 'Jue' },
  { value: 5, label: 'Viernes', shortLabel: 'Vie' },
  { value: 6, label: 'Sábado', shortLabel: 'Sáb' },
  { value: 0, label: 'Domingo', shortLabel: 'Dom' },
];

const DEFAULT_FORM_VALUES: ScheduleRuleFormValues = {
  dayOfWeek: 1,
  startTime: '08:00',
  endTime: '09:00',
  type: '',
  accessLevel: 'normal',
  professorId: 0,
  academyId: 0,
  maxSeats: 30,
};

const ACCESS_LEVEL_LABELS: Record<LessonAccessLevel, string> = {
  normal: 'NORMAL',
  pro: 'PRO',
  morning: 'MAÑANA',
};

function formatProfessorName(professor?: User) {
  if (!professor) {
    return 'Profesor sin asignar';
  }

  return `${professor.firstName} ${professor.lastName}`;
}

function buildFormValuesFromRule(rule: LessonScheduleRule): ScheduleRuleFormValues {
  return {
    id: rule.id,
    dayOfWeek: rule.dayOfWeek,
    startTime: rule.startTime,
    endTime: rule.endTime,
    type: rule.type,
    accessLevel: rule.accessLevel ?? 'normal',
    professorId: rule.professorId,
    academyId: rule.academyId,
    maxSeats: rule.maxSeats,
  };
}

function hasInvalidTimeRange(values: ScheduleRuleFormValues) {
  return values.endTime <= values.startTime;
}

export default function InstructorScheduleTemplateEditor({
  academies,
  academyId,
  createRequestKey,
  scheduleRules,
}: InstructorScheduleTemplateEditorProps) {
  const { data: instructors } = useGetUsers({ instructor: true });
  const createScheduleRuleMutation = useCreateLessonScheduleRule();
  const updateScheduleRuleMutation = useUpdateLessonScheduleRule();
  const deleteScheduleRuleMutation = useDeleteLessonScheduleRule();
  const [formValues, setFormValues] = useState<ScheduleRuleFormValues | null>(null);
  const [rulePendingDisable, setRulePendingDisable] = useState<LessonScheduleRule | null>(null);

  const professorById = useMemo(() => {
    return new Map((instructors ?? []).map((instructor) => [instructor.id, instructor]));
  }, [instructors]);

  const scheduleRulesByDay = useMemo(() => {
    const rulesMap = new Map<number, LessonScheduleRule[]>();

    WEEK_DAYS.forEach((day) => rulesMap.set(day.value, []));
    scheduleRules.forEach((rule) => {
      const dayRules = rulesMap.get(rule.dayOfWeek) ?? [];
      dayRules.push(rule);
      rulesMap.set(rule.dayOfWeek, dayRules);
    });

    rulesMap.forEach((dayRules) => {
      dayRules.sort((a, b) => {
        if (a.startTime === b.startTime) {
          return a.type.localeCompare(b.type);
        }

        return a.startTime.localeCompare(b.startTime);
      });
    });

    return rulesMap;
  }, [scheduleRules]);

  const firstInstructorId = instructors?.[0]?.id ?? 0;
  const selectedAcademy = academies.find((academy) => academy.id === academyId);
  const canCreateRules = firstInstructorId > 0 && academies.length > 0;

  const openCreateDialog = (dayOfWeek = 1) => {
    setFormValues({
      ...DEFAULT_FORM_VALUES,
      dayOfWeek,
      academyId,
      professorId: firstInstructorId,
    });
  };

  useEffect(() => {
    if (createRequestKey > 0 && canCreateRules) {
      openCreateDialog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createRequestKey]);

  const openEditDialog = (rule: LessonScheduleRule) => {
    setFormValues(buildFormValuesFromRule(rule));
  };

  const closeDialog = () => {
    setFormValues(null);
  };

  const updateFormValue = <Key extends keyof ScheduleRuleFormValues>(
    key: Key,
    value: ScheduleRuleFormValues[Key],
  ) => {
    setFormValues((currentValues) => {
      if (!currentValues) {
        return currentValues;
      }

      return {
        ...currentValues,
        [key]: value,
      };
    });
  };

  const handleSubmit = () => {
    if (!formValues || hasInvalidTimeRange(formValues)) {
      return;
    }

    const payload: LessonScheduleRulePayload = {
      dayOfWeek: formValues.dayOfWeek,
      startTime: formValues.startTime,
      endTime: formValues.endTime,
      type: formValues.type,
      accessLevel: formValues.accessLevel,
      professorId: formValues.professorId,
      academyId: formValues.academyId,
      maxSeats: formValues.maxSeats,
    };

    if (formValues.id) {
      updateScheduleRuleMutation.mutate({ id: formValues.id, changeset: payload });
    } else {
      createScheduleRuleMutation.mutate(payload);
    }

    closeDialog();
  };

  const isFormValid =
    formValues !== null &&
    formValues.type.trim() !== '' &&
    formValues.professorId > 0 &&
    formValues.academyId > 0 &&
    formValues.maxSeats > 0 &&
    !hasInvalidTimeRange(formValues);

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2 },
          mb: 2,
          borderRadius: 4,
          border: '1px solid rgba(18,18,18,0.08)',
          backgroundColor: 'white',
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2.5,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: '#111111',
                  color: 'white',
                }}
              >
                <EventRepeatRoundedIcon fontSize="small" />
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#111111' }}>
                  Plantilla semanal
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(17,17,17,0.56)' }}>
                  {`${selectedAcademy?.name ?? 'Academia'} · se usa para generar la semana completa`}
                </Typography>
              </Box>
            </Stack>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(164px, 1fr))',
              gap: 1.25,
              overflowX: 'auto',
              pb: 0.5,
            }}
          >
            {WEEK_DAYS.map((day) => {
              const dayRules = scheduleRulesByDay.get(day.value) ?? [];

              return (
                <Box
                  key={day.value}
                  sx={{
                    minWidth: 164,
                    minHeight: 320,
                    borderRadius: 4,
                    border: '1px solid rgba(18,18,18,0.08)',
                    bgcolor: dayRules.length === 0 ? '#fafafa' : 'white',
                  }}
                >
                  <Stack sx={{ height: '100%' }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        px: 1.5,
                        py: 1.25,
                        borderBottom: '1px solid rgba(18,18,18,0.08)',
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontWeight: 800, color: '#111111', lineHeight: 1.1 }}>
                          {day.shortLabel}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'rgba(17,17,17,0.52)', fontWeight: 700 }}
                        >
                          {`${dayRules.length} ${dayRules.length === 1 ? 'clase' : 'clases'}`}
                        </Typography>
                      </Box>

                      <Tooltip title={`Añadir ${day.label.toLowerCase()}`}>
                        <IconButton
                          aria-label={`Añadir ${day.label.toLowerCase()}`}
                          size="small"
                          onClick={() => openCreateDialog(day.value)}
                          sx={{
                            width: 32,
                            height: 32,
                            border: '1px solid rgba(18,18,18,0.08)',
                            color: '#111111',
                          }}
                        >
                          <AddRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    <Stack spacing={1} sx={{ p: 1, flex: 1 }}>
                      {dayRules.length === 0 ? (
                        <Box
                          sx={{
                            height: '100%',
                            minHeight: 210,
                            borderRadius: 3,
                            border: '1px dashed rgba(17,17,17,0.14)',
                            display: 'grid',
                            placeItems: 'center',
                            px: 1.25,
                            textAlign: 'center',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: 'rgba(17,17,17,0.44)', fontWeight: 600 }}
                          >
                            Sin plantilla
                          </Typography>
                        </Box>
                      ) : (
                        dayRules.map((rule) => {
                          const professor = professorById.get(rule.professorId);

                          return (
                            <Box
                              key={rule.id}
                              sx={{
                                p: 1.25,
                                borderRadius: 3,
                                bgcolor: '#f7f7f7',
                                border: '1px solid rgba(18,18,18,0.08)',
                                transition:
                                  'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                                '&:hover': {
                                  transform: 'translateY(-1px)',
                                  boxShadow: '0 10px 18px rgba(17,17,17,0.08)',
                                  borderColor: 'rgba(18,18,18,0.16)',
                                },
                              }}
                            >
                              <Stack spacing={0.75}>
                                <Stack direction="row" justifyContent="space-between" spacing={1}>
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 800, color: '#111111' }}
                                    >
                                      {`${rule.startTime} - ${rule.endTime}`}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: '#111111',
                                        fontWeight: 700,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {rule.type}
                                    </Typography>
                                    {rule.accessLevel !== 'normal' && (
                                      <Typography
                                        variant="caption"
                                        sx={{ color: '#6d28d9', fontWeight: 800 }}
                                      >
                                        {ACCESS_LEVEL_LABELS[rule.accessLevel]}
                                      </Typography>
                                    )}
                                  </Box>

                                  <Stack direction="row" spacing={0.25}>
                                    <Tooltip title="Editar">
                                      <IconButton
                                        aria-label="Editar bloque"
                                        size="small"
                                        onClick={() => openEditDialog(rule)}
                                        sx={{ width: 28, height: 28, color: '#111111' }}
                                      >
                                        <EditRoundedIcon sx={{ fontSize: 17 }} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Eliminar">
                                      <IconButton
                                        aria-label="Eliminar bloque"
                                        size="small"
                                        onClick={() => setRulePendingDisable(rule)}
                                        color="error"
                                        sx={{ width: 28, height: 28 }}
                                      >
                                        <DeleteOutlineRoundedIcon sx={{ fontSize: 17 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                </Stack>

                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: 'block',
                                    color: 'rgba(17,17,17,0.56)',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {formatProfessorName(professor)}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{ color: 'rgba(17,17,17,0.56)', fontWeight: 800 }}
                                >
                                  {`${rule.maxSeats} plazas`}
                                </Typography>
                              </Stack>
                            </Box>
                          );
                        })
                      )}
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </Stack>
      </Paper>

      <Dialog open={formValues !== null} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800 }}>
          {formValues?.id ? 'Editar bloque semanal' : 'Nuevo bloque semanal'}
        </DialogTitle>
        {formValues && (
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="schedule-day-label">Día</InputLabel>
                <Select
                  labelId="schedule-day-label"
                  label="Día"
                  value={formValues.dayOfWeek}
                  onChange={(event) => updateFormValue('dayOfWeek', Number(event.target.value))}
                >
                  {WEEK_DAYS.map((day) => (
                    <MenuItem key={day.value} value={day.value}>
                      {day.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  label="Inicio"
                  type="time"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={formValues.startTime}
                  onChange={(event) => updateFormValue('startTime', event.target.value)}
                />
                <TextField
                  label="Fin"
                  type="time"
                  fullWidth
                  size="small"
                  error={hasInvalidTimeRange(formValues)}
                  helperText={hasInvalidTimeRange(formValues) ? 'La hora fin debe ser posterior' : ''}
                  InputLabelProps={{ shrink: true }}
                  value={formValues.endTime}
                  onChange={(event) => updateFormValue('endTime', event.target.value)}
                />
              </Stack>

              <TextField
                label="Tipo"
                fullWidth
                size="small"
                value={formValues.type}
                onChange={(event) => updateFormValue('type', event.target.value)}
              />

              <FormControl fullWidth size="small">
                <InputLabel id="schedule-access-level-label">Acceso</InputLabel>
                <Select
                  labelId="schedule-access-level-label"
                  label="Acceso"
                  value={formValues.accessLevel}
                  onChange={(event) =>
                    updateFormValue('accessLevel', event.target.value as LessonAccessLevel)
                  }
                >
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="pro">Pro</MenuItem>
                  <MenuItem value="morning">Mañana</MenuItem>
                </Select>
              </FormControl>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <FormControl fullWidth size="small">
                  <InputLabel id="schedule-professor-label">Profesor</InputLabel>
                  <Select
                    labelId="schedule-professor-label"
                    label="Profesor"
                    value={formValues.professorId}
                    onChange={(event) => updateFormValue('professorId', Number(event.target.value))}
                  >
                    {(instructors ?? []).map((instructor) => (
                      <MenuItem key={instructor.id} value={instructor.id}>
                        {formatProfessorName(instructor)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Plazas"
                  type="number"
                  size="small"
                  sx={{ width: { xs: '100%', sm: 128 } }}
                  inputProps={{ min: 1 }}
                  value={formValues.maxSeats}
                  onChange={(event) => updateFormValue('maxSeats', Number(event.target.value))}
                />
              </Stack>

              <FormControl fullWidth size="small">
                <InputLabel id="schedule-academy-label">Academia</InputLabel>
                <Select
                  labelId="schedule-academy-label"
                  label="Academia"
                  value={formValues.academyId}
                  onChange={(event) => updateFormValue('academyId', Number(event.target.value))}
                >
                  {academies.map((academy) => (
                    <MenuItem key={academy.id} value={academy.id}>
                      {academy.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} sx={{ textTransform: 'none', color: '#111111' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!isFormValid}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              bgcolor: '#111111',
              boxShadow: 'none',
              '&:hover': { bgcolor: '#222222', boxShadow: 'none' },
            }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rulePendingDisable !== null}
        onClose={() => setRulePendingDisable(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Eliminar bloque</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'rgba(17,17,17,0.72)' }}>
            {rulePendingDisable
              ? `${rulePendingDisable.startTime} - ${rulePendingDisable.endTime} · ${rulePendingDisable.type}`
              : ''}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setRulePendingDisable(null)}
            sx={{ textTransform: 'none', color: '#111111' }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              if (rulePendingDisable) {
                deleteScheduleRuleMutation.mutate({ id: rulePendingDisable.id });
              }
              setRulePendingDisable(null);
            }}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              boxShadow: 'none',
            }}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
